// What the report's Audience layer knows about an audience's data — statuses and the segment /
// influencer normalisers — kept here so it doesn't depend on the Audiences module
// (Audiences/utils/audienceUtils), which may be dropped. Same contracts and output shapes as
// that module, for the same endpoints.

import { isApiSuccess } from '../../../../constants/platformStatusCodes';
import { compactNumber } from './formatNumbers';

// ─── Statuses ─────────────────────────────────────────────────────────────────

/** An audience's own status (GET /audiences/{id}/segments → `status`). */
export const AUDIENCE_STATUS = {
  INITIATED: 'Initiated',
  PROCESSING: 'Processing',
  SEGMENTS_READY: 'SegmentsReady',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};

export const NON_TERMINAL_AUDIENCE_STATUSES = [
  AUDIENCE_STATUS.INITIATED,
  AUDIENCE_STATUS.PROCESSING,
  AUDIENCE_STATUS.SEGMENTS_READY,
];

/**
 * Influencer generation, shared by the segment (`segments[].influencersStatus`) and the
 * influencer list (`data.status`).
 */
export const SEGMENT_INFLUENCERS_STATUS = {
  GENERATING: 'generating',
  READY: 'ready',
  UNAVAILABLE: 'unavailable',
};

const SEGMENT_INFLUENCERS_STATUS_VALUES = new Set(Object.values(SEGMENT_INFLUENCERS_STATUS));

/** A segment's own enrichment (`segments[].enrichmentStatus`) values that mean it's still running. */
const ENRICHMENT_IN_PROGRESS = ['queued', 'processing'];

/**
 * Whether a segment is still being enriched. generate-more sent during enrichment is dropped by
 * the backend, so Regenerate waits for this. A failed enrichment counts as finished.
 * @param {{ enrichmentStatus: (string|null) }} segment
 * @returns {boolean}
 */
export const isSegmentEnriching = (segment) =>
  ENRICHMENT_IN_PROGRESS.includes(segment.enrichmentStatus);

/** Absent and unknown values are null — rendered as "no status" (no spinner, no polling). */
const normalizeInfluencersStatus = (value) => {
  const v = typeof value === 'string' ? value.toLowerCase() : null;
  return SEGMENT_INFLUENCERS_STATUS_VALUES.has(v) ? v : null;
};

// ─── Segments ─────────────────────────────────────────────────────────────────

/** The entry with the highest weight of a [{ label, weight }] list. */
const topEntry = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.reduce((best, current) => (current.weight > best.weight ? current : best), list[0]);
};

/** 0–1 weight → "61%" / "60.5%". */
const weightToPercent = (weight) => {
  const value = weight * 100;
  return `${value % 1 === 0 ? String(Math.round(value)) : value.toFixed(1)}%`;
};

/** A segment's display tags from `respData` (size, income, location, gender, ethnicity, …). */
const buildSegmentTags = (respData = {}) => {
  const tags = [];

  const estimate = respData.segmentSize?.absolute_estimate;
  if (estimate) {
    tags.push({
      key: 'size',
      value: `${compactNumber(estimate.low)}–${compactNumber(estimate.high)}`,
    });
  }

  const demographics = respData.demographicsV3 ?? {};

  const income = topEntry(demographics.income);
  const incomeLabel = income?.label ?? income?.bucket;
  if (incomeLabel) tags.push({ key: 'income', value: incomeLabel });

  const location = demographics.location?.[0]?.label;
  if (location) tags.push({ key: 'location', value: location });

  const gender = topEntry(demographics.gender);
  if (gender?.label) {
    tags.push({ key: 'gender', value: `${gender.label} ${weightToPercent(gender.weight)}` });
  }

  const ethnicity = topEntry(demographics.ethnicity);
  if (ethnicity?.label) {
    tags.push({
      key: 'ethnicity',
      value: `${ethnicity.label} ${weightToPercent(ethnicity.weight)}`,
    });
  }

  const education = topEntry(demographics.education);
  if (education?.label) tags.push({ key: 'education', value: education.label });

  const age = topEntry(demographics.age);
  if (age?.bucket) tags.push({ key: 'age', value: age.bucket });

  return tags;
};

/**
 * The short title of a segment for display. The model labels segments "<title>: <tagline>" (e.g.
 * "Modern Millennial Parents: Connected, mindful caretakers") and needs that full label as context
 * when regenerating, so it stays as is in the data; the UI shows the part before the first ": ".
 * A name without ": " (or with nothing before it) is returned whole.
 * @param {string} name
 * @returns {string}
 */
export const segmentTitle = (name) => {
  const [title] = String(name).split(': ');
  return title.trim() || name;
};

/**
 * One segment of GET /audiences/{id}/segments. `id` is the `segmentId` (the positional id every
 * segment endpoint takes); `dbId` keeps the row's primary key.
 */
export function normalizeAudienceSegment(segment) {
  const respData = segment.respData ?? {};
  return {
    id: String(segment.segmentId ?? segment.id ?? ''),
    dbId: String(segment.id ?? segment.segmentId ?? ''),
    name: segment.customName ?? segment.name ?? 'Untitled segment',
    description:
      segment.whyThisFits ??
      respData.outline?.description ??
      respData.summary?.textGenerate ??
      segment.description ??
      '',
    thumbnail: segment.imageS3Path ?? segment.imageUrl ?? segment.thumbnail ?? null,
    enrichmentStatus: segment.enrichmentStatus ?? null,
    enrichmentMessage: segment.enrichmentMessage ?? null,
    // Independent of enrichmentStatus: a segment can be enriched while its influencers generate.
    influencersStatus: normalizeInfluencersStatus(segment.influencersStatus),
    selectionStatus: segment.status ?? null,
    archiveStatus: segment.archiveStatus ?? 'Active',
    isArchived: segment.isArchived ?? segment.archiveStatus === 'Archived',
    tags: segment.tags ?? buildSegmentTags(respData),
    respData,
  };
}

// ─── Segment influencers ──────────────────────────────────────────────────────

const toSnakeCase = (key) => key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

/**
 * The score breakdown arrives as an object or a JSON string, camel- or snake-cased; the score
 * components read snake_case. Anything unparseable is "not available yet" (null).
 */
const parseScoreBreakdown = (value) => {
  let breakdown = value;
  if (!breakdown) return null;
  if (typeof breakdown === 'string') {
    try {
      breakdown = JSON.parse(breakdown);
    } catch (e) {
      return null;
    }
  }
  if (typeof breakdown !== 'object' || Array.isArray(breakdown)) return null;
  return Object.entries(breakdown).reduce((acc, [key, val]) => {
    acc[toSnakeCase(key)] = val;
    return acc;
  }, {});
};

/** One influencer row → the list's shape. Metric fields may all be null (renderers are null-safe). */
const normalizeSegmentInfluencer = (row, index = 0) => {
  const metrics = row?.metrics ?? {};
  const handle = row?.handle ?? '';
  const platform = row?.platform ?? '';
  // Unique per row: the same creator can appear on two platforms in one segment.
  const key = row?.id != null ? String(row.id) : `inf-${platform}-${handle || index}`;
  return {
    id: row?.id != null ? String(row.id) : key,
    key,
    name: row?.fullName ?? handle ?? '',
    username: handle,
    avatar: row?.profilePic ?? null,
    profileUrl: row?.profileUrl ?? null,
    platform: row?.platform ?? null,
    influencerType: row?.influencerType ?? null,
    budget: row?.budget ?? null,
    rationale: row?.rationale ?? '',
    score: row?.score ?? null,
    scoreBreakdown: parseScoreBreakdown(row?.scoreBreakdown ?? row?.score_breakdown),
    affiliationLinks: Array.isArray(row?.affiliationLinks) ? row.affiliationLinks : [],
    brandSafety: row?.brandSafety ?? null,
    // The pair the shared Brand Safety badge reads, and the creator report id it keys off.
    brandSafetyCheck: row?.brandSafetyCheck ?? null,
    creatorReportDetailsId: row?.creatorReportDetailsId ?? null,
    // Brand-safety helpers key on `socialNetwork`; audience rows spell it `platform`.
    socialNetwork: row?.platform ?? null,
    malePct: row?.audienceGenders?.male ?? null,
    femalePct: row?.audienceGenders?.female ?? null,
    followers: metrics.followers ?? null,
    erRate: metrics.engagementRate ?? null,
    avgLikes: metrics.avgLikes ?? null,
    cpe: metrics.cpe ?? null,
    postCount: metrics.postCount ?? null,
    status: null,
    audienceAge: null,
    // Archived rows are flagged, not removed — they still come back in the list.
    archived: Boolean(row?.archived),
    locked: false,
  };
};

/**
 * GET …/segments/{id}/influencers → { status, isTrial, upgradeRequired, planTarget, count,
 * influencers, error }. A 404 reads as "unavailable" (existence isn't leaked).
 */
export function normalizeSegmentInfluencersResponse(resp) {
  // eslint-disable-next-line no-underscore-dangle -- api_client may attach _httpStatus
  const httpStatus = resp?.httpStatus ?? resp?._httpStatus ?? null;
  const empty = {
    isTrial: false,
    upgradeRequired: false,
    planTarget: 0,
    count: 0,
    influencers: [],
  };

  if (httpStatus === 404) {
    return { ...empty, status: SEGMENT_INFLUENCERS_STATUS.UNAVAILABLE, error: null };
  }
  if (!isApiSuccess(resp) || !resp?.data) {
    return {
      ...empty,
      status: null,
      error: resp?.error ?? resp?.message ?? 'Failed to load influencers.',
    };
  }

  const { data } = resp;
  const rows = Array.isArray(data.influencers) ? data.influencers : [];
  return {
    status: data.status ?? null,
    isTrial: Boolean(data.isTrial),
    upgradeRequired: Boolean(data.upgradeRequired),
    planTarget: data.planTarget ?? 0,
    count: data.count ?? rows.length,
    influencers: rows.map(normalizeSegmentInfluencer),
    error: null,
  };
}
